import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import AnimalCard from './AnimalCard.js';
import MiniAnimalCard from './MiniAnimalCard.js';
import OasisCard from './OasisCard.js';
import {
  placeCardAndResolveMovement,
  flipCardAndResolveMovement,
  getMovementChoice,
  formedTriplet,
  columnCapacity,
  resolveChameleonStrength,
  columnStrength,
  applyScorpion,
  scorpionWeakestCandidates,
  scorpionTieIsTrivial,
  diffOasisRow,
  reconstructBeforeRow,
} from '../common/boardEngine.js';
import { describeMove, describeFlipCascade } from '../common/describeMove.js';
import { ANIMAL_LABEL } from '../common/oasisCatalog.js';

const CARD_SLOTS = 4;
// Must match .oasis-board-cards's flex `gap` in App.scss.
const SLOT_GAP_REM = 0.25;
// Pacing for auto-advancing through round-end's already-resolved history
// (see the roundEnd* state/effects below) -- fast enough not to feel
// sluggish, slow enough to actually read as "these flip one at a time."
const ROUND_END_STEP_MS = 900;

// How long each step of an incoming-move (or round-end replay) animation
// holds before advancing to the next one, and how long the CSS transition
// for that step's own motion takes -- kept in one place so the whole
// feel of the animation can be tuned from here. `holdMs` must be >= the
// matching `playMs` so a step's motion always finishes before the next one
// starts; the extra gap after `playMs` is a deliberate beat to let the eye
// register what just happened.
const MOVE_ANIMATION_TIMING = {
  enter: { playMs: 450, holdMs: 700 },
  move: { playMs: 450, holdMs: 700 },
  killMark: { playMs: 0, holdMs: 500 },
  killRemove: { playMs: 0, holdMs: 150 },
  flip: { playMs: 500, holdMs: 700 },
};

function columnKeyFor(side) {
  return side === 'my' ? 'myColumn' : 'opponentColumn';
}

// Content-based key for a set of cardIds -- order-independent, so it can be
// compared across renders even when the array itself is a fresh reference
// (Firestore delivers a new array on every snapshot, even an unchanged one).
function idsKey(ids) {
  return [...ids].sort().join(',');
}

// Every side of `oasisIndex` where a just-placed/flipped Scorpion's own
// weakest-candidate check is a genuine tie (2+ different cards -- possibly
// different animals, e.g. another Scorpion and a Chameleon both currently
// resolving to strength 0 -- at the same lowest strength) -- each needs its
// own player choice, exactly like an ambiguous attraction pull already does.
function detectScorpionTies(row, oasisIndex, scorpionCardId) {
  return ['my', 'opponent']
    .map(side => ({ side, candidates: scorpionWeakestCandidates(row, oasisIndex, scorpionCardId, side) }))
    .filter(t => t.candidates.length > 1 && !scorpionTieIsTrivial(t.candidates));
}

// Replays every already-committed play (in order, movement/Scorpion
// included) on top of `baseRow` -- used to get an accurate "board before
// this specific pending play" snapshot from scratch, since the live
// `oasisRow` while a pull choice is in progress already has that same
// play's own (still-default, not-yet-chosen) Scorpion kill baked in and
// can't be reused directly as a "before" state for re-checking it.
function accumulateCommittedPlays(baseRow, committedPlays, hand) {
  let row = baseRow;
  for (const p of committedPlays) {
    const card = hand.find(c => c.cardId === p.cardId);
    if (!card) continue;
    const placedCard = { cardId: card.cardId, animal: card.animal, face: p.face, orientation: p.orientation ?? null };
    row = placeCardAndResolveMovement(row, p.side, p.oasisIndex, placedCard, p.pullChoice);
    if (p.face === 'up' && card.animal === 'scorpion') {
      row = applyScorpion(row, p.oasisIndex, card.cardId, p.scorpionTieBreak);
    }
  }
  return row;
}

// Locates a card currently on the board by id, in the shape animation steps
// need (side/oasisIndex/face/orientation alongside animal) -- an opponent's
// still-face-down card is redacted (no `animal`) exactly like everywhere
// else `view.oasisRow` is read.
function locateCardInRow(row, cardId) {
  for (let oasisIndex = 0; oasisIndex < row.length; oasisIndex++) {
    const oasis = row[oasisIndex];
    const mine = oasis.myColumn.find(c => c.cardId === cardId);
    if (mine) return { cardId, oasisIndex, side: 'my', animal: mine.animal, face: mine.face, orientation: mine.orientation ?? null };
    const theirs = oasis.opponentColumn.find(c => c.cardId === cardId);
    if (theirs) {
      return {
        cardId,
        oasisIndex,
        side: 'opponent',
        animal: theirs.animal,
        face: theirs.face,
        orientation: theirs.orientation ?? null,
      };
    }
  }
  return null;
}

function cloneOasisRow(row) {
  return row.map(o => ({
    ...o,
    palmTreeOwnerIds: [...o.palmTreeOwnerIds],
    myColumn: [...o.myColumn],
    opponentColumn: [...o.opponentColumn],
  }));
}

// Interleaves each "unit" (a placed card, or a flipped card) with whatever
// moves/kills its own placement/flip specifically triggered, so a 2nd card's
// entrance never has to wait for an earlier one's effects to catch up --
// mirrors the real engine's per-play order (place -> resolve this card's own
// attraction pull -> resolve this card's own Scorpion kill -> next card),
// attributing each moved/removed card to whichever unit's own column it
// belongs to (attraction always pulls into the just-acted column; a
// Scorpion's kill always happens in its own column).
function interleaveEffectSteps(units, movedCards, removedCards) {
  const steps = [];
  const remainingMoved = [...movedCards];
  const remainingRemoved = [...removedCards];
  for (const unit of units) {
    steps.push(unit.enterStep);
    for (let i = remainingMoved.length - 1; i >= 0; i--) {
      const m = remainingMoved[i];
      // Same-animal match required, not just destination column/side -- 2
      // placed/flipped cards sharing a column (e.g. an Oryx and a Jackal
      // both landing in Column 1) would otherwise be ambiguous by position
      // alone, and attraction only ever pulls a card matching the animal
      // that triggered it.
      if (m.toOasisIndex === unit.oasisIndex && m.toSide === unit.side && m.animal === unit.animal) {
        steps.push({ type: 'move', card: m });
        remainingMoved.splice(i, 1);
      }
    }
    if (unit.isScorpion) {
      const killed = [];
      for (let i = remainingRemoved.length - 1; i >= 0; i--) {
        const r = remainingRemoved[i];
        if (r.fromOasisIndex === unit.oasisIndex && r.fromSide === unit.side) {
          killed.push(r);
          remainingRemoved.splice(i, 1);
        }
      }
      if (killed.length > 0) {
        steps.push({ type: 'killMark', cards: killed });
        steps.push({ type: 'killRemove', cards: killed });
      }
    }
  }
  // Anything left unattributed (shouldn't normally happen) still gets shown,
  // just without a specific unit to sequence it right after.
  for (const m of remainingMoved) steps.push({ type: 'move', card: m });
  if (remainingRemoved.length > 0) {
    steps.push({ type: 'killMark', cards: remainingRemoved });
    steps.push({ type: 'killRemove', cards: remainingRemoved });
  }
  return steps;
}

// Builds the ordered list of animation steps for one move's own effect
// (entrance + this move's own pulls/Scorpion kill, each resolved right after
// its own card lands -- see interleaveEffectSteps), optionally followed by a
// round-end flip cascade's own steps in the same interleaved shape.
// `resolvedRow` (the fully-revealed post-cascade board) is only needed for a
// flip cascade, to look up each flipped card's true animal/orientation for
// the 3D flip-reveal animation -- flippedCardIds alone doesn't carry that.
function buildAnimationSteps({ placedCards = [], movedCards = [], removedCards = [], flipCascade = null, resolvedRow = null }) {
  const placedUnits = placedCards.map(card => ({
    enterStep: { type: 'enter', card },
    oasisIndex: card.oasisIndex,
    side: card.side,
    animal: card.animal,
    isScorpion: card.face === 'up' && card.animal === 'scorpion',
  }));
  const steps = interleaveEffectSteps(placedUnits, movedCards, removedCards);

  if (flipCascade) {
    const flipUnits = flipCascade.flippedCardIds.map(cardId => {
      const located = resolvedRow ? locateCardInRow(resolvedRow, cardId) : null;
      return {
        enterStep: { type: 'flip', cardId, animal: located?.animal ?? null, orientation: located?.orientation ?? null },
        oasisIndex: located?.oasisIndex,
        side: located?.side,
        animal: located?.animal ?? null,
        isScorpion: located?.animal === 'scorpion',
      };
    });
    steps.push(...interleaveEffectSteps(flipUnits, flipCascade.movedCards, flipCascade.removedCards));
  }
  return steps;
}

// Applies one animation step to a working oasisRow, returning a new row --
// used to derive what the board should look like after N of the sequence's
// steps have played, same idea as boardEngine's placeCardAndResolveMovement
// but replaying an already-known diff instead of computing a fresh one.
function applyAnimationStep(row, step) {
  const next = cloneOasisRow(row);
  if (step.type === 'enter') {
    const c = step.card;
    const key = columnKeyFor(c.side);
    next[c.oasisIndex][key] = [
      ...next[c.oasisIndex][key],
      { cardId: c.cardId, animal: c.animal, face: c.face, orientation: c.orientation ?? null },
    ];
  } else if (step.type === 'move') {
    const c = step.card;
    const fromKey = columnKeyFor(c.fromSide);
    const toKey = columnKeyFor(c.toSide);
    const fromColumn = next[c.fromOasisIndex][fromKey];
    const idx = fromColumn.findIndex(x => x.cardId === c.cardId);
    if (idx !== -1) {
      const [moved] = fromColumn.splice(idx, 1);
      const toKeyColumn = next[c.toOasisIndex][toKey];
      next[c.toOasisIndex][toKey] = [...toKeyColumn, moved];
    }
  } else if (step.type === 'killRemove') {
    for (const c of step.cards) {
      const key = columnKeyFor(c.fromSide);
      next[c.fromOasisIndex][key] = next[c.fromOasisIndex][key].filter(x => x.cardId !== c.cardId);
    }
  } else if (step.type === 'flip') {
    for (const oasis of next) {
      for (const side of ['myColumn', 'opponentColumn']) {
        const idx = oasis[side].findIndex(x => x.cardId === step.cardId);
        if (idx !== -1) {
          // Replace (not spread) the existing entry -- an opponent's
          // face-down card is redacted in the working row (no `animal`
          // field), so just toggling `face` would leave it face-up with no
          // animal. The step itself carries the true animal/orientation
          // (looked up from the fully-resolved board when the sequence was
          // built), which round-end can always safely reveal.
          oasis[side][idx] = {
            cardId: step.cardId,
            animal: step.animal,
            face: 'up',
            orientation: step.orientation,
          };
        }
      }
    }
  }
  // killMark is a marker step only (see killingCardIds below) -- the row
  // itself doesn't change until the matching killRemove step.
  return next;
}

// Applies an in-progress flip-order selection (see flipOrderSelection) to a
// base oasisRow, one chosen card at a time -- each flip resolves its own
// pull/Scorpion kill right away, exactly like the server will once Done is
// pressed. Shared by roundEndPreviewRow (the instantly-computed full
// preview) and the animation-step builder below (which needs the same
// logic for just the single newly-added card).
function applyFlipOrderSelection(baseRow, selection) {
  let row = baseRow;
  for (const cardId of selection) {
    const oasisIndex = row.findIndex(o => o.myColumn.some(c => c.cardId === cardId));
    if (oasisIndex === -1) continue; // already moved/consumed by an earlier pull in this same preview
    row = flipCardAndResolveMovement(row, 'my', oasisIndex, cardId);
    const flipped = row[oasisIndex].myColumn.find(c => c.cardId === cardId);
    if (flipped?.animal === 'scorpion') {
      row = applyScorpion(row, oasisIndex, cardId);
    }
  }
  return row;
}

/**
 * Presentational + self-contained-selection board. No upfront "pick an
 * action" step -- the player just selects a card and places it, and the
 * action (A/B/C) is inferred from where it lands:
 *   - placed face-up on my side  -> partial action A, needs a 2nd card
 *   - placed face-up on opponent -> action B, complete
 *   - placed face-down on my side -> action C, complete
 * Nothing is sent to the server until "Submit Move"; a "Reset" button
 * clears the in-progress turn at any point after the first card is placed.
 * Hovering a hand card (or having one selected) previews which columns are
 * actually legal targets for it right now -- capacity-aware, so the 4th
 * slot a column gets from 3 Jackals only ever shows as available when that
 * rule genuinely applies to this specific card/column.
 */
export default function OasisBoard({
  view,
  myTurn: rawMyTurn,
  myName,
  opponentName,
  round,
  myScore,
  opponentScore,
  opponentHandCount,
  myPalmTreesRemaining,
  opponentPalmTreesRemaining,
  deckCount,
  burnedCard,
  lastMoveCardIds = [],
  revealResult,
  roundSummary,
  onNextRound,
  nextRoundButtonLabel,
  nextRoundDisabled,
  onSubmitMove,
  submitting,
  roundEndResolution,
  onSubmitRoundEndChoice,
  lastRoundResult,
  lastMoveResult,
}) {
  // --- Incoming-move / round-end animation (see MOVE_ANIMATION_TIMING and
  // buildAnimationSteps/applyAnimationStep above). `incomingAnimation` is
  // non-null only while an opponent's/bot's move, or the just-finished
  // round's ending move + flip cascade, is actively playing out; `beforeRow`
  // is the board as it stood right before that move, `steps` the ordered
  // effects to replay, `stepIndex` how many have been applied so far.
  const [incomingAnimation, setIncomingAnimation] = useState(null);
  // The most recently built animation (mine's never stored here -- only
  // opponent/bot moves and round-end sequences animate), kept around after
  // it finishes so the Replay button can re-run it; replaced wholesale the
  // next time something newer happens, same lifetime as lastMoveCardIds.
  const [lastReplay, setLastReplay] = useState(null);
  // Plain-English description of the most recent move (mine or the
  // opponent's/bot's) or round-end result -- shown regardless of whether
  // that move animates.
  const [moveSummary, setMoveSummary] = useState(null);
  const previousOasisRowRef = useRef(view.oasisRow);
  const cardNodeRefs = useRef(new Map());
  const handMiniRef = useRef(null);
  // Rects captured just before an 'enter'/'move' step's state update, keyed
  // by cardId, for the following useLayoutEffect's FLIP invert+play.
  const pendingFlipRef = useRef(null);
  // Which move/round lastMoveCardIds-driven processing has already handled
  // -- content-keyed (not identity-keyed), since Firestore delivers a fresh
  // array reference on every snapshot even when the contents didn't change.
  const processedMoveKeyRef = useRef(idsKey(lastMoveCardIds));
  // Which round the round-end effect has already handled.
  const animatedRoundRef = useRef(null);
  // True once this component has rendered at least one genuinely live
  // (non-review) frame -- guards against animating a round-end "from"
  // nothing if the component happens to mount straight into a review screen
  // (e.g. a page refresh mid-round-summary).
  const hasSeenLiveRef = useRef(!revealResult);
  // Animations detected while a previous one is still playing (e.g. the
  // practice bot chaining a bonus turn right after its first move) wait
  // here instead of preempting/canceling what's on screen -- each entry
  // already carries its own correctly-captured beforeRow/steps (see
  // enqueueAnimation below), so there's nothing stale about queuing rather
  // than dropping them.
  const animationQueueRef = useRef([]);
  // The round this board is currently showing -- used only to detect the
  // transition to a new round (see the reset effect below).
  const roundRef = useRef(round);
  // Which move the mount-time lastMoveResult seed effect has already
  // applied -- opponentName can still be '' on the very first render or
  // two (it loads via a separate fetch, after the "Loading…" gate has
  // already let the board itself through), so that effect has to be able
  // to retry once names are actually ready rather than run exactly once.
  const seededMoveKeyRef = useRef(null);

  useEffect(() => {
    if (!revealResult) hasSeenLiveRef.current = true;
  }, [revealResult]);

  // A brand new round just started (acknowledging the previous one's
  // summary): cardIds are re-numbered from scratch each round (see
  // functions/deal.js's buildAnimalDeck), so a stale beforeRow/processed-key
  // from the round that just ended could otherwise get diffed against the
  // new round's fresh board and produce a nonsense "everything changed"
  // summary the first time this happens. Clear every piece of animation
  // state and let the board settle on the new round's real (empty) deal
  // before anything is allowed to animate again. useLayoutEffect so this
  // always runs (and wins) before the detection effects below in the rare
  // case a round change and a move landed in the same commit.
  useLayoutEffect(() => {
    if (roundRef.current === round) return;
    roundRef.current = round;
    previousOasisRowRef.current = view.oasisRow;
    processedMoveKeyRef.current = idsKey(lastMoveCardIds);
    animationQueueRef.current = [];
    setIncomingAnimation(null);
    setLastReplay(null);
    setMoveSummary(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  // Starts `entry` right away if nothing's currently playing, otherwise
  // queues it -- uses the functional setState form so this is correct even
  // when called from an effect whose own closure's `incomingAnimation` might
  // already be stale (e.g. two detection effects firing in the same commit).
  function enqueueAnimation(entry) {
    setIncomingAnimation(current => {
      if (current) {
        animationQueueRef.current.push(entry);
        return current;
      }
      return { ...entry, stepIndex: 0 };
    });
  }

  // Seeds the move-summary/Replay from the server-persisted lastMoveResult
  // on mount -- there's no in-memory "before" board to diff against at that
  // point (a fresh page load, or navigating in from elsewhere), but the
  // server already computed and persisted this move's own breakdown (see
  // functions/gameFlow.js), and reconstructBeforeRow can rebuild an
  // accurate "before" snapshot purely from that + the current board, so
  // both the summary text and a real Replay survive a refresh. Waits for
  // opponentName specifically (retrying via the dep below) rather than
  // running exactly once on mount -- it can still be '' for the first
  // render or two (a separate fetch, not covered by the page's own
  // "Loading…" gate), and locking in a blank actor name permanently would
  // otherwise never self-correct.
  useLayoutEffect(() => {
    if (revealResult || roundEndResolution) return;
    if (lastMoveCardIds.length === 0 || !lastMoveResult || !opponentName) return;
    const resultKey = idsKey(lastMoveResult.placedCards.map(c => c.cardId));
    if (resultKey !== idsKey(lastMoveCardIds)) return;
    if (seededMoveKeyRef.current === resultKey) return;
    seededMoveKeyRef.current = resultKey;

    const { placedCards, movedCards, removedCards, actingSide } = lastMoveResult;
    const actorName = actingSide === 'my' ? 'You' : opponentName;
    const parts = describeMove({ placedCards, movedCards, removedCards }, actorName, actingSide, opponentName);
    setMoveSummary({ parts, key: resultKey });

    const beforeRow = reconstructBeforeRow(view.oasisRow, { placedCards, movedCards, removedCards });
    const steps = buildAnimationSteps({ placedCards, movedCards, removedCards });
    if (steps.length > 0) {
      setLastReplay({ kind: 'move', beforeRow, steps, actorSide: actingSide ?? 'opponent', key: resultKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opponentName]);

  // A genuine incoming move (opponent's, or the bot's in Practice Game):
  // diff the board, build a move summary either way, and -- only when it
  // isn't confirming my own just-submitted move -- animate it. beforeRow is
  // captured and previousOasisRowRef advanced *immediately*, regardless of
  // whether an earlier animation is still playing -- so a move that arrives
  // before the previous one has finished still gets an accurate "before"
  // snapshot (its own diff never blends with another move's), it just waits
  // its turn in the queue above rather than playing out of order.
  //
  // useLayoutEffect, not useEffect: view.oasisRow already reflects the
  // fully-resolved move the instant this render happens, so without this
  // running *before* the browser paints, there'd be one visible frame of
  // the final placed state before this kicks in and snaps back to
  // beforeRow to start the animation -- a flicker.
  useLayoutEffect(() => {
    if (revealResult || roundEndResolution) return;
    const key = idsKey(lastMoveCardIds);
    if (key === processedMoveKeyRef.current) return;
    processedMoveKeyRef.current = key;
    if (lastMoveCardIds.length === 0) return;

    const beforeRow = previousOasisRowRef.current;
    previousOasisRowRef.current = view.oasisRow;

    const isMine = Boolean(pendingSubmissionCardIds) && lastMoveCardIds.some(id => pendingSubmissionCardIds.includes(id));
    const actorName = isMine ? 'You' : opponentName;
    const actorSide = isMine ? 'my' : 'opponent';
    const placedCards = lastMoveCardIds.map(id => locateCardInRow(view.oasisRow, id)).filter(Boolean);
    const diff = diffOasisRow(beforeRow, view.oasisRow, lastMoveCardIds);
    const parts = describeMove(
      { placedCards, movedCards: diff.movedCards, removedCards: diff.removedCards },
      actorName,
      actorSide,
      opponentName
    );
    setMoveSummary({ parts, key });

    // actorSide here isn't derived from placedCards[0].side, which for an
    // action B is whichever side the card *landed on* (mine, since that's
    // an attack), not who actually played it.
    const steps = buildAnimationSteps({ placedCards, movedCards: diff.movedCards, removedCards: diff.removedCards });
    if (steps.length > 0) {
      const entry = { kind: 'move', beforeRow, steps, actorSide, key };
      // Replay is offered for every move, mine included -- but a move I
      // just made isn't auto-played live, since I already watched it
      // happen via my own optimistic preview the instant I submitted it.
      setLastReplay(entry);
      if (!isMine) enqueueAnimation(entry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMoveCardIds, revealResult, roundEndResolution]);

  // The round-ending move + flip cascade: same idea, triggered once the
  // round has fully resolved (revealResult flips true with a new
  // lastRoundResult), independent of whose move actually ended it.
  // useLayoutEffect for the same flicker reason as the live-move effect
  // above -- view.oasisRow already reflects the fully-resolved round the
  // instant reviewPending flips true.
  useLayoutEffect(() => {
    if (!revealResult || !lastRoundResult) return;
    if (animatedRoundRef.current === lastRoundResult.round) return;
    animatedRoundRef.current = lastRoundResult.round;

    const beforeRow = previousOasisRowRef.current;
    previousOasisRowRef.current = lastRoundResult.oasisRow;

    const placedCards = lastRoundResult.placedCards ?? [];
    const ownMoved = lastRoundResult.movedCards ?? [];
    const ownRemoved = lastRoundResult.removedCards ?? [];
    const flipCascade = lastRoundResult.flipCascade ?? null;
    // Who actually made the round-ending move -- NOT placedCards[0].side,
    // which for an action B is whichever side the card landed *on* (the
    // opponent's column, for an attack), not who played it. Falls back to
    // the old (imperfect) inference only for a game predating this field.
    const actorSide = lastRoundResult.actingSide ?? placedCards[0]?.side ?? ownMoved[0]?.fromSide ?? 'opponent';
    const actorName = actorSide === 'my' ? 'You' : opponentName;
    const nameFor = side => (side === 'my' ? 'You' : opponentName);

    const moveParts = describeMove(
      { placedCards, movedCards: ownMoved, removedCards: ownRemoved },
      actorName,
      actorSide,
      opponentName
    );
    const cascadeParts = flipCascade ? describeFlipCascade(flipCascade, lastRoundResult.oasisRow, nameFor) : [];
    // A period between the move and the cascade, not just a space -- these
    // are 2 separate sentences ("...in Column 4. Their Elephant flipped..."),
    // unless the move's own text already ended with one (the "made no
    // board change." fallback).
    const lastMovePart = moveParts[moveParts.length - 1];
    const moveEndsWithPeriod = typeof lastMovePart === 'string' && lastMovePart.trim().endsWith('.');
    const parts =
      cascadeParts.length > 0 ? [...moveParts, moveEndsWithPeriod ? ' ' : '. ', ...cascadeParts] : moveParts;
    const key = `round-${lastRoundResult.round}`;
    setMoveSummary({ parts, key });

    // Keeps the live-move effect above from re-animating this same move
    // once we're back on the live board after acknowledging -- its cardIds
    // reappear unchanged in lastMoveCardIds until a genuinely new move happens.
    if (placedCards.length > 0) processedMoveKeyRef.current = idsKey(placedCards.map(c => c.cardId));

    if (!hasSeenLiveRef.current) return;

    // If my own move ended the round, there's nothing to replay for that
    // part -- I already watched it happen live in my own optimistic
    // preview. Only the flip cascade (which reveals cards and effects
    // nobody has seen yet, regardless of whose move triggered it) animates.
    const steps = buildAnimationSteps({
      placedCards: actorSide === 'my' ? [] : placedCards,
      movedCards: actorSide === 'my' ? [] : ownMoved,
      removedCards: actorSide === 'my' ? [] : ownRemoved,
      flipCascade,
      resolvedRow: lastRoundResult.oasisRow,
    });
    if (steps.length === 0) return;
    enqueueAnimation({ kind: 'roundEnd', beforeRow, steps, actorSide, key });
    setLastReplay({ kind: 'roundEnd', beforeRow, steps, actorSide, key });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealResult, lastRoundResult]);

  // Advances the animation one step at a time, capturing each moving card's
  // (or, for an entrance, the opponent hand-mini's) rect immediately before
  // the state update that will relocate it -- the following useLayoutEffect
  // picks that up to run the actual FLIP invert+play once React has
  // re-rendered into the new position.
  useEffect(() => {
    if (!incomingAnimation) return undefined;
    if (incomingAnimation.stepIndex >= incomingAnimation.steps.length) return undefined;
    const step = incomingAnimation.steps[incomingAnimation.stepIndex];
    const timing = MOVE_ANIMATION_TIMING[step.type];
    const timer = setTimeout(() => {
      if (step.type === 'enter' || step.type === 'move') {
        const cardId = step.card.cardId;
        const node = cardNodeRefs.current.get(cardId);
        const originRect = node ? node.getBoundingClientRect() : handMiniRef.current?.getBoundingClientRect();
        pendingFlipRef.current = originRect ? new Map([[cardId, originRect]]) : null;
      } else {
        pendingFlipRef.current = null;
      }
      setIncomingAnimation(a => (a ? { ...a, stepIndex: a.stepIndex + 1 } : a));
    }, timing.holdMs);
    return () => clearTimeout(timer);
  }, [incomingAnimation]);

  // Once every step has played, either start whatever's next in the queue
  // (a move that arrived while this one was still playing) or, if the queue
  // is empty, stop overriding the board -- it renders the real settled
  // state again (view.oasisRow, or lastRoundResult.oasisRow via the
  // parent's reviewPending display), which the replayed steps always land
  // on exactly since they're just a stored diff being replayed.
  useEffect(() => {
    if (incomingAnimation && incomingAnimation.stepIndex >= incomingAnimation.steps.length) {
      const next = animationQueueRef.current.shift();
      setIncomingAnimation(next ? { ...next, stepIndex: 0 } : null);
    }
  }, [incomingAnimation]);

  // The actual FLIP "invert + play": right after React re-renders the card
  // at its new resting position, jump it back (untransitioned) to where it
  // visually was, then release it into a transitioning move to (0,0) --
  // i.e. its real position -- so it visibly slides there.
  useLayoutEffect(() => {
    const firstRects = pendingFlipRef.current;
    if (!firstRects) return;
    pendingFlipRef.current = null;
    const step = incomingAnimation?.steps[incomingAnimation.stepIndex - 1];
    const playMs = step ? MOVE_ANIMATION_TIMING[step.type].playMs : 300;
    for (const [cardId, firstRect] of firstRects) {
      const node = cardNodeRefs.current.get(cardId);
      if (!node) continue;
      const lastRect = node.getBoundingClientRect();
      const dx = firstRect.left - lastRect.left;
      const dy = firstRect.top - lastRect.top;
      if (dx === 0 && dy === 0) continue;
      node.style.transition = 'none';
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      // Force a reflow so the browser registers the inverted position
      // before the transition below is enabled -- otherwise it would just
      // animate from (0,0), never showing the inverted starting point.
      // eslint-disable-next-line no-unused-expressions
      node.offsetHeight;
      node.style.transition = `transform ${playMs}ms ease`;
      node.style.transform = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingAnimation?.stepIndex]);

  function handleReplay() {
    if (!lastReplay || incomingAnimation) return;
    // Clear any leftover inline transform/transition from the original
    // playthrough before starting fresh -- these are set directly on the
    // DOM node (outside React's own render), so they'd otherwise persist
    // into the replay's first frame.
    for (const node of cardNodeRefs.current.values()) {
      node.style.transition = '';
      node.style.transform = '';
    }
    enqueueAnimation(lastReplay);
  }

  const animationWorkingRow = useMemo(() => {
    if (!incomingAnimation) return null;
    let row = incomingAnimation.beforeRow;
    for (let i = 0; i < incomingAnimation.stepIndex; i++) {
      row = applyAnimationStep(row, incomingAnimation.steps[i]);
    }
    return row;
  }, [incomingAnimation]);

  const currentAnimStep =
    incomingAnimation && incomingAnimation.stepIndex < incomingAnimation.steps.length
      ? incomingAnimation.steps[incomingAnimation.stepIndex]
      : null;
  const killingCardIds =
    currentAnimStep?.type === 'killMark' ? new Set(currentAnimStep.cards.map(c => c.cardId)) : null;
  // The step itself carries the true animal/orientation (see
  // buildAnimationSteps) -- the working row's own entry for this card is
  // still whatever it was *before* the flip (redacted, for an opponent's
  // card), so the 3D flip's reveal face has to come from the step, not `card`.
  const flippingCard = currentAnimStep?.type === 'flip' ? currentAnimStep : null;

  // While a round-end is being interactively resolved (see roundEndResolution below),
  // or while the round-ending move/cascade is still animating, nobody plays a card --
  // overridden here rather than trusted from the caller's `myTurn` prop, since
  // currentTurnPlayerId deliberately stays frozen (unchanged) through resolution, which
  // would otherwise still read as "my turn" for whichever side happened to hold it, and
  // an opponent's move animation shouldn't be interactable either even once it's
  // technically my turn again.
  const myTurn = rawMyTurn && !roundEndResolution && !incomingAnimation;
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  // AnimalCard's onMouseEnter/onMouseLeave are plain JS mouse-event
  // handlers, not CSS :hover -- so scoping :hover in CSS behind
  // @media(hover:hover) doesn't stop them from firing on a touch tap too.
  // iOS Safari's touch-to-mouse-event emulation then treats the first tap
  // on anything with a mouseenter handler as *only* entering hover state
  // (showing the dashed target preview here, since that's driven by
  // hoveredCardId) and requires a 2nd tap to actually fire click. Only
  // wiring the hover handlers on genuinely hover-capable pointers avoids
  // triggering that quirk in the first place.
  const [supportsHover] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
  );
  const [plays, setPlays] = useState([]); // [{ cardId, oasisIndex, side, face, orientation }]
  // undefined = no explicit choice made yet (submitting now defaults to skip);
  // null = "Skip" explicitly clicked; a number = that oasis index chosen.
  const [palmTreeChoice, setPalmTreeChoice] = useState(undefined);
  // Set when placing a card would pull in more matching neighbors than the
  // column has room for -- the play itself isn't finalized into `plays`
  // until the player picks exactly which ones move.
  const [pendingPullChoice, setPendingPullChoice] = useState(null);
  // Set when a just-placed face-up Scorpion's own kill ties on one or both
  // sides of its column (see detectScorpionTies) -- like pendingPullChoice,
  // the play isn't finalized into `plays` until every tied side has a
  // chosen cardId. `resolvedTieBreak` accumulates as `{ my?, opponent? }`.
  const [pendingScorpionTieChoice, setPendingScorpionTieChoice] = useState(null);
  // Set right after a submit succeeds, holding the cardIds just sent --
  // kept around (with `plays` still populated, so the optimistic preview
  // keeps showing) until lastMoveCardIds actually confirms the realtime
  // view has caught up. The callable resolving only means the write
  // happened server-side, not that this component's own Firestore listener
  // has delivered it yet -- clearing `plays` right away races that
  // delivery and can flash back to the stale pre-submission board for a
  // beat (or, if the listener happens to lag further, longer than that).
  const [pendingSubmissionCardIds, setPendingSubmissionCardIds] = useState(null);

  function resetTurn() {
    setSelectedCardId(null);
    setPlays([]);
    setPalmTreeChoice(undefined);
    setPendingPullChoice(null);
    setPendingScorpionTieChoice(null);
    setPendingSubmissionCardIds(null);
  }

  // Once enough cards have been chosen to fill the available room, finalize
  // the deferred play into `plays` with that explicit pull order -- unless
  // the now-fully-placed Scorpion's own kill also ties, in which case that
  // needs resolving first (see the effect below) before this play is
  // actually done.
  useEffect(() => {
    if (pendingPullChoice && pendingPullChoice.chosenCardIds.length === pendingPullChoice.fitCount) {
      const p = pendingPullChoice.playDraft;
      const card = view.hand.find(c => c.cardId === p.cardId);
      if (card?.animal === 'scorpion' && p.face === 'up') {
        const placedCard = { cardId: card.cardId, animal: card.animal, face: 'up', orientation: p.orientation ?? null };
        const beforeThisPlay = accumulateCommittedPlays(view.oasisRow, plays, view.hand);
        const afterPull = placeCardAndResolveMovement(beforeThisPlay, p.side, p.oasisIndex, placedCard, pendingPullChoice.chosenCardIds);
        const ties = detectScorpionTies(afterPull, p.oasisIndex, card.cardId);
        if (ties.length > 0) {
          setPendingScorpionTieChoice({
            playDraft: { ...p, pullChoice: pendingPullChoice.chosenCardIds },
            ties,
            resolvedTieBreak: {},
          });
          setPendingPullChoice(null);
          return;
        }
      }
      setPlays(prev => [...prev, { ...pendingPullChoice.playDraft, pullChoice: pendingPullChoice.chosenCardIds }]);
      setPendingPullChoice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPullChoice]);

  // Once every tied side has a chosen cardId, finalize the play into
  // `plays` with that scorpionTieBreak attached.
  useEffect(() => {
    if (
      pendingScorpionTieChoice &&
      pendingScorpionTieChoice.ties.every(t => pendingScorpionTieChoice.resolvedTieBreak[t.side])
    ) {
      setPlays(prev => [
        ...prev,
        { ...pendingScorpionTieChoice.playDraft, scorpionTieBreak: pendingScorpionTieChoice.resolvedTieBreak },
      ]);
      setPendingScorpionTieChoice(null);
    }
  }, [pendingScorpionTieChoice]);

  function chooseScorpionTie(cardId) {
    setPendingScorpionTieChoice(current => {
      if (!current) return current;
      const pendingTie = current.ties.find(t => !current.resolvedTieBreak[t.side]);
      if (!pendingTie || !pendingTie.candidates.some(c => c.cardId === cardId)) return current;
      return { ...current, resolvedTieBreak: { ...current.resolvedTieBreak, [pendingTie.side]: cardId } };
    });
  }

  // Clears the in-progress turn once lastMoveCardIds confirms the realtime
  // view has actually caught up to the move just submitted (see
  // handleSubmit) -- this is the real signal, not the callable resolving.
  useEffect(() => {
    if (pendingSubmissionCardIds && pendingSubmissionCardIds.every(id => lastMoveCardIds.includes(id))) {
      resetTurn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMoveCardIds, pendingSubmissionCardIds]);

  // Safety net only, for a listener that's *slow*, not necessarily broken:
  // by the time we're here the callable already resolved, so the move
  // genuinely succeeded server-side -- reverting the board (via resetTurn,
  // which clears `plays`) would show a false "it didn't go through" state
  // on top of a move that actually did. On a slow connection specifically,
  // the listener can legitimately take longer than the old 8s cutoff,
  // which was exactly this bug: cards visibly "returning to hand" even
  // though the move had already succeeded. So this only re-enables
  // Reset/Done (in case the listener genuinely never recovers and the
  // player wants to manually intervene) -- it deliberately leaves `plays`
  // alone, so the correct optimistic preview keeps showing either way
  // until the real confirmation above eventually clears it.
  useEffect(() => {
    if (!pendingSubmissionCardIds) return undefined;
    const timer = setTimeout(() => setPendingSubmissionCardIds(null), 20000);
    return () => clearTimeout(timer);
  }, [pendingSubmissionCardIds]);

  // --- Round-end interactive resolution (see functions/gameFlow.js's
  // advanceRoundEndResolution) -- auto-plays through whatever the last
  // server round-trip already resolved (recentSteps), pausing once caught
  // up AND a real decision is pending. Candidates are highlighted directly
  // on their real board cards below (in renderColumnZone), not in a
  // separate strip, so the player is always looking at the actual column.
  const roundEndPendingStep = roundEndResolution?.pendingStep;
  const [roundEndAnimatedCount, setRoundEndAnimatedCount] = useState(0);
  const [roundEndChosenPullIds, setRoundEndChosenPullIds] = useState([]);
  const [roundEndSubmitting, setRoundEndSubmitting] = useState(false);
  // Flip order is picked as a full batch before anything is actually sent:
  // flipOrderSelection is the in-progress, purely local order (nothing
  // committed yet -- Reset just clears this); flipOrderQueue is the
  // confirmed order still waiting to be submitted one at a time, once
  // Done is pressed.
  const [flipOrderSelection, setFlipOrderSelection] = useState([]);
  const [flipOrderQueue, setFlipOrderQueue] = useState(null);

  const flipOrderSignature =
    roundEndPendingStep?.kind === 'flipOrder' ? [...roundEndPendingStep.candidateCardIds].sort().join(',') : null;

  useEffect(() => {
    setRoundEndAnimatedCount(0);
  }, [roundEndResolution?.recentSteps]);

  useEffect(() => {
    const steps = roundEndResolution?.recentSteps;
    if (!steps || roundEndAnimatedCount >= steps.length) return undefined;
    const timer = setTimeout(() => setRoundEndAnimatedCount(c => c + 1), ROUND_END_STEP_MS);
    return () => clearTimeout(timer);
  }, [roundEndAnimatedCount, roundEndResolution?.recentSteps]);

  useEffect(() => {
    setRoundEndChosenPullIds([]);
  }, [roundEndPendingStep]);

  // A fresh flip-order decision (a genuinely different candidate set, e.g.
  // the next tier down) starts the local selection over.
  useEffect(() => {
    setFlipOrderSelection([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipOrderSignature]);

  // Drains flipOrderQueue one card at a time, but only ever advances in
  // response to the server's own confirmed state (pendingStep actually
  // narrowing/moving on) rather than assuming our own request landed the
  // instant its promise resolves -- avoids submitting the next queued
  // choice against a decision the server hasn't caught up to yet.
  useEffect(() => {
    if (!flipOrderQueue) return;
    if (roundEndPendingStep?.kind === 'flipOrder' && roundEndPendingStep.deciderSide === 'me') {
      const stillQueued = flipOrderQueue.filter(id => roundEndPendingStep.candidateCardIds.includes(id));
      if (stillQueued.length !== flipOrderQueue.length) {
        setFlipOrderQueue(stillQueued.length > 0 ? stillQueued : null);
      }
    } else if (flipOrderQueue.length > 0) {
      setFlipOrderQueue(null);
    }
  }, [roundEndPendingStep, flipOrderQueue]);

  useEffect(() => {
    if (!flipOrderQueue || flipOrderQueue.length === 0 || roundEndSubmitting) return;
    if (roundEndPendingStep?.kind !== 'flipOrder' || roundEndPendingStep.deciderSide !== 'me') return;
    if (!roundEndPendingStep.candidateCardIds.includes(flipOrderQueue[0])) return;
    submitRoundEndChoice('flipOrder', flipOrderQueue[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipOrderQueue, roundEndPendingStep, roundEndSubmitting]);

  const roundEndCaughtUp = roundEndAnimatedCount >= (roundEndResolution?.recentSteps.length ?? 0);
  // While a flip-order choice is being built up, the normal Reset/Done
  // buttons are repurposed for it instead of adding a separate pair --
  // there's never a live turn in progress at the same time anyway.
  const flipOrderActive =
    roundEndCaughtUp &&
    roundEndPendingStep?.kind === 'flipOrder' &&
    roundEndPendingStep.deciderSide === 'me' &&
    !flipOrderQueue;
  // Nothing useful to say while flipOrderQueue silently drains (the board
  // itself already shows each card resolving) -- no status row for it.
  const flipOrderSubmittingQueue = roundEndPendingStep?.kind === 'flipOrder' && Boolean(flipOrderQueue);

  async function submitRoundEndChoice(kind, choice) {
    setRoundEndSubmitting(true);
    try {
      await onSubmitRoundEndChoice(kind, choice);
    } finally {
      setRoundEndSubmitting(false);
    }
  }

  function toggleRoundEndPull(cardId) {
    if (roundEndSubmitting || !roundEndPendingStep) return;
    setRoundEndChosenPullIds(current => {
      if (current.includes(cardId)) return current.filter(id => id !== cardId);
      if (current.length >= roundEndPendingStep.fitCount) return current;
      const next = [...current, cardId];
      if (next.length === roundEndPendingStep.fitCount) submitRoundEndChoice('pullChoice', next);
      return next;
    });
  }

  function toggleFlipOrderCard(cardId) {
    if (flipOrderQueue) return; // already confirmed, mid-submission
    setFlipOrderSelection(current =>
      current.includes(cardId) ? current.filter(id => id !== cardId) : [...current, cardId]
    );
  }

  const inferredAction =
    plays.length === 0 ? null : plays[0].side === 'opponent' ? 'B' : plays[0].face === 'down' ? 'C' : 'A';

  // Normally action A needs exactly 2 plays -- but once the deck is empty
  // and only 1 card is left in hand, there's no 2nd card ever coming, so
  // that lone card can be played face-up by itself.
  const requiredActionAPlays = view.hand.length === 1 && deckCount === 0 ? 1 : 2;
  // Mirrors the server's canPlaceFaceDown: at most one more face-down card
  // than the opponent currently has -- checked against the confirmed board
  // state (not the in-progress simulated one) since it's about what's
  // already committed, not this turn's draft plays.
  const myFaceDownCount = view.oasisRow.reduce(
    (sum, o) => sum + o.myColumn.filter(c => c.face === 'down').length,
    0
  );
  const opponentFaceDownCount = view.oasisRow.reduce(
    (sum, o) => sum + o.opponentColumn.filter(c => c.face === 'down').length,
    0
  );
  const canPlayFaceDown = myFaceDownCount <= opponentFaceDownCount;
  const isComplete =
    inferredAction === 'B' || inferredAction === 'C' || (inferredAction === 'A' && plays.length === requiredActionAPlays);
  const canSelectCard =
    myTurn &&
    !isComplete &&
    !pendingPullChoice &&
    !pendingScorpionTieChoice &&
    (inferredAction !== 'A' || plays.length < requiredActionAPlays);

  function handleSelectHandCard(cardId) {
    if (!canSelectCard) return;
    setSelectedCardId(current => (current === cardId ? null : cardId));
  }

  function handlePickTarget(side, oasisIndex, face) {
    if (!selectedCardId || isComplete || pendingPullChoice || pendingScorpionTieChoice) return;
    if (inferredAction === 'A' && side !== 'my') return;

    const card = view.hand.find(c => c.cardId === selectedCardId);
    const orientation = card.animal === 'chameleon' ? 'right' : null;
    const playDraft = { cardId: selectedCardId, oasisIndex, side, face, orientation };

    if (face === 'up') {
      const placedCard = { cardId: card.cardId, animal: card.animal, face, orientation };
      const { eligible, fitCount, needsChoice } = getMovementChoice(oasisRow, side, oasisIndex, placedCard);
      if (needsChoice) {
        setPendingPullChoice({ playDraft, eligible, fitCount, chosenCardIds: [] });
        setSelectedCardId(null);
        return;
      }
      if (card.animal === 'scorpion') {
        // No pull ambiguity, so `oasisRow` here is already exactly "the
        // board before this card" (plays doesn't include it yet) -- safe
        // to reuse directly, unlike the pull-choice finalize effect above.
        const afterPull = placeCardAndResolveMovement(oasisRow, side, oasisIndex, placedCard, undefined);
        const ties = detectScorpionTies(afterPull, oasisIndex, card.cardId);
        if (ties.length > 0) {
          setPendingScorpionTieChoice({ playDraft, ties, resolvedTieBreak: {} });
          setSelectedCardId(null);
          return;
        }
      }
    }

    setPlays([...plays, playDraft]);
    setSelectedCardId(null);
  }

  function toggleChosenPullCard(cardId) {
    setPendingPullChoice(current => {
      if (!current) return current;
      if (current.chosenCardIds.includes(cardId)) {
        return { ...current, chosenCardIds: current.chosenCardIds.filter(id => id !== cardId) };
      }
      if (current.chosenCardIds.length >= current.fitCount) return current;
      return { ...current, chosenCardIds: [...current.chosenCardIds, cardId] };
    });
  }

  function setOrientation(cardId, orientation) {
    setPlays(plays.map(p => (p.cardId === cardId ? { ...p, orientation } : p)));
  }

  // Picking up a card that's already been placed this turn (but not yet
  // submitted) pulls it back out of `plays` and re-selects it, so the board
  // shows all its legal targets again -- exactly like selecting it from the
  // hand -- without forcing a full Reset of the turn.
  function handlePickUpPlay(play) {
    if (!myTurn) return;
    setPlays(plays.filter(p => p.cardId !== play.cardId));
    setSelectedCardId(play.cardId);
  }

  function toggleFace(cardId) {
    setPlays(plays.map(p => {
      if (p.cardId !== cardId) return p;
      const face = p.face === 'down' ? 'up' : 'down';
      const card = view.hand.find(c => c.cardId === cardId);
      const orientation = card?.animal === 'chameleon' && face === 'up' ? p.orientation ?? 'right' : null;
      return { ...p, face, orientation };
    }));
  }

  // A staged play's card can briefly point at a cardId no longer in
  // view.hand -- e.g. right after a successful submit, if the real-time
  // Firestore update (with the card already removed) lands before this
  // component's own post-submit resetTurn() runs. Treat that as "not ready
  // yet" rather than crashing; the imminent reset clears it a tick later.
  const readyToSubmit =
    isComplete && plays.every(p => {
      const card = view.hand.find(c => c.cardId === p.cardId);
      return Boolean(card) && (card.animal !== 'chameleon' || p.orientation);
    });

  // Actually simulate each play in order (movement/attraction included) so
  // the board reflects the real resulting state, not a guess.
  const { simulatedOasisRow, touchedMyIndexes, pendingRemovedCards } = useMemo(() => {
    let row = view.oasisRow;
    const touched = [];
    // Cards an unsubmitted Scorpion play would destroy -- kept out of `row`
    // (so capacity/strength math already reflects the removal, matching the
    // server) but reported separately so the board can still *display* them
    // in place, marked for removal, until the move is actually submitted.
    const removed = [];
    const collectRemovals = (beforeEntry, afterEntry, oasisIndex) => {
      for (const side of ['my', 'opponent']) {
        const key = side === 'my' ? 'myColumn' : 'opponentColumn';
        beforeEntry[key].forEach((c, index) => {
          if (!afterEntry[key].some(ac => ac.cardId === c.cardId)) {
            removed.push({ cardId: c.cardId, card: c, oasisIndex, side, index });
          }
        });
      }
    };
    for (const p of plays) {
      const card = view.hand.find(c => c.cardId === p.cardId);
      if (!card) continue; // stale play referencing a card view.hand no longer has -- see readyToSubmit above
      const placedCard = { cardId: card.cardId, animal: card.animal, face: p.face, orientation: p.orientation ?? null };
      row = placeCardAndResolveMovement(row, p.side, p.oasisIndex, placedCard, p.pullChoice);
      // Mirrors applyPlay's ordering server-side: a face-up Scorpion's own
      // destructive power resolves immediately, right after movement -- so
      // a 2nd card played later this same turn sees the freed-up slot.
      if (p.face === 'up' && card.animal === 'scorpion') {
        const before = row[p.oasisIndex];
        row = applyScorpion(row, p.oasisIndex, card.cardId, p.scorpionTieBreak);
        collectRemovals(before, row[p.oasisIndex], p.oasisIndex);
      }
      // A face-down play doesn't count toward a same-turn triplet either --
      // that only happens once it's flipped face-up at round end.
      if (p.side === 'my' && p.face === 'up') touched.push(p.oasisIndex);
    }
    if (pendingPullChoice) {
      // The card is placed on the board right away, and whichever
      // candidates have been chosen so far are actually pulled in live --
      // an explicit (possibly empty) pullChoice array pulls exactly those,
      // so the preview grows incrementally as the player picks. Its own
      // Scorpion kill (if any) isn't previewed here even when it ties --
      // that's what the pendingScorpionTieChoice block below is for, once
      // this pull choice is actually finished.
      const p = pendingPullChoice.playDraft;
      const card = view.hand.find(c => c.cardId === p.cardId);
      if (card) {
        const placedCard = { cardId: card.cardId, animal: card.animal, face: p.face, orientation: p.orientation ?? null };
        row = placeCardAndResolveMovement(row, p.side, p.oasisIndex, placedCard, pendingPullChoice.chosenCardIds);
        if (p.face === 'up' && card.animal === 'scorpion') {
          const before = row[p.oasisIndex];
          row = applyScorpion(row, p.oasisIndex, card.cardId);
          collectRemovals(before, row[p.oasisIndex], p.oasisIndex);
        }
      }
    }
    if (pendingScorpionTieChoice) {
      // Same idea as pendingPullChoice above: the card is already on the
      // board, and each side's kill previews live as its own choice is
      // made -- applyScorpion falls back to the first candidate (by column
      // order) for whichever side isn't decided yet, so the preview always
      // shows *a* valid outcome even mid-decision.
      const p = pendingScorpionTieChoice.playDraft;
      const card = view.hand.find(c => c.cardId === p.cardId);
      if (card) {
        const placedCard = { cardId: card.cardId, animal: card.animal, face: p.face, orientation: p.orientation ?? null };
        row = placeCardAndResolveMovement(row, p.side, p.oasisIndex, placedCard, p.pullChoice);
        const before = row[p.oasisIndex];
        row = applyScorpion(row, p.oasisIndex, card.cardId, pendingScorpionTieChoice.resolvedTieBreak);
        collectRemovals(before, row[p.oasisIndex], p.oasisIndex);
      }
    }
    return { simulatedOasisRow: row, touchedMyIndexes: touched, pendingRemovedCards: removed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plays, pendingPullChoice, pendingScorpionTieChoice]);

  const tripletOasisIndexes = [...new Set(touchedMyIndexes)].filter(idx =>
    formedTriplet(simulatedOasisRow[idx].myColumn)
  );

  async function handleSubmit() {
    const payloadPlays = plays.map(p => ({
      cardId: p.cardId,
      oasisIndex: p.oasisIndex,
      ...(view.hand.find(c => c.cardId === p.cardId)?.animal === 'chameleon' ? { orientation: p.orientation } : {}),
      ...(p.pullChoice ? { pullChoice: p.pullChoice } : {}),
      ...(p.scorpionTieBreak ? { scorpionTieBreak: p.scorpionTieBreak } : {}),
    }));
    // Set *before* the round-trip, not after it resolves -- the realtime
    // listener can otherwise deliver the confirmed move first (it's an
    // independent channel from this callable's own response), and the
    // incoming-move animation effect uses this to tell "my own move" from
    // a genuine incoming one; missing that window means my own move gets
    // mistaken for an opponent's and animated.
    setPendingSubmissionCardIds(payloadPlays.map(p => p.cardId));
    try {
      await onSubmitMove({
        action: inferredAction,
        plays: payloadPlays,
        palmTree: typeof palmTreeChoice === 'number' ? { oasisIndex: palmTreeChoice } : undefined,
      });
      // Don't clear the in-progress turn yet -- the callable resolving only
      // means the write happened server-side, not that this component's
      // own realtime listener has delivered it. `plays` stays populated
      // (keeping the optimistic preview on screen) until lastMoveCardIds
      // actually confirms the view caught up, in the effect below.
    } catch {
      // The submission never actually landed -- don't leave Reset/Done
      // disabled waiting for a confirmation that's never coming.
      setPendingSubmissionCardIds(null);
    }
  }

  // Previews the player's in-progress flip-order choice as if each chosen
  // card were already flipped -- pulls/Scorpion kills happen right away in
  // this preview, exactly like the server will actually apply them once
  // Done is pressed, rather than just marking the cards as "chosen" while
  // the rest of the board sits frozen. The instant end state this settles
  // on; the effect below plays the same underlying change through the
  // normal FLIP animation for whichever card was *just* added.
  const roundEndPreviewRow = useMemo(() => {
    if (!roundEndResolution) return null;
    return applyFlipOrderSelection(roundEndResolution.oasisRow, flipOrderSelection);
  }, [roundEndResolution, flipOrderSelection]);

  // Animates a newly-picked flip-order card's own flip + pull/kill the same
  // way an incoming move or the round-end cascade itself animates, instead
  // of the preview just silently snapping to its new state -- lets the
  // player actually watch what each candidate does before committing to it.
  // Only fires for a plain append (one more card chosen); deselecting a
  // card, or the selection resetting for a new decision, just lets
  // roundEndPreviewRow's instant recompute above show the new state, since
  // there's nothing meaningful to play backwards.
  // useLayoutEffect, not useEffect: roundEndPreviewRow above already
  // recomputes to the full new state in the very same render that adds a
  // card to the selection -- enqueuing here has to happen before paint, or
  // the final state would flash on screen for a frame before this
  // animation's own beforeRow/step0 takes over.
  const prevFlipOrderSelectionRef = useRef([]);
  useLayoutEffect(() => {
    const prev = prevFlipOrderSelectionRef.current;
    prevFlipOrderSelectionRef.current = flipOrderSelection;
    if (!roundEndResolution) return;
    const isAppend =
      flipOrderSelection.length === prev.length + 1 && prev.every((id, i) => id === flipOrderSelection[i]);
    if (!isAppend) return;

    const cardId = flipOrderSelection[flipOrderSelection.length - 1];
    const beforeRow = applyFlipOrderSelection(roundEndResolution.oasisRow, prev);
    const oasisIndex = beforeRow.findIndex(o => o.myColumn.some(c => c.cardId === cardId));
    if (oasisIndex === -1) return;

    let afterRow = flipCardAndResolveMovement(beforeRow, 'my', oasisIndex, cardId);
    const flipped = afterRow[oasisIndex].myColumn.find(c => c.cardId === cardId);
    if (flipped?.animal === 'scorpion') {
      afterRow = applyScorpion(afterRow, oasisIndex, cardId);
    }
    const diff = diffOasisRow(beforeRow, afterRow, [cardId]);
    const steps = buildAnimationSteps({
      flipCascade: { flippedCardIds: [cardId], movedCards: diff.movedCards, removedCards: diff.removedCards },
      resolvedRow: afterRow,
    });
    if (steps.length > 0) {
      enqueueAnimation({ kind: 'flipOrderPreview', beforeRow, steps, actorSide: 'my', key: `flip-order-preview-${cardId}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipOrderSelection, roundEndResolution]);

  const oasisRow = animationWorkingRow
    ? animationWorkingRow
    : roundEndResolution
    ? roundEndPreviewRow
    : plays.length > 0 || pendingPullChoice || pendingScorpionTieChoice
    ? simulatedOasisRow
    : view.oasisRow;

  // The round summary/points reveal and "Completed" header text wait for
  // the round-end animation to finish playing -- showing the final score
  // while the cascade is still visibly resolving would spoil/contradict
  // what's on screen.
  const roundEndAnimating = incomingAnimation?.kind === 'roundEnd';
  const revealResultNow = revealResult && !roundEndAnimating;
  const roundSummaryNow = roundEndAnimating ? null : roundSummary;
  // "Resolving…"/"Completed" describe the *round's* status, not whose turn
  // it is -- stays in the header, grouped with Round # (see
  // oasis-board-status below). "Your Turn"/"Opponent's Turn" is genuinely
  // about whose turn it is, so it shows next to my own name instead (see
  // oasis-board-my-name-row) -- only while there actually is a "turn" to
  // speak of (i.e. not while the round is resolving/completed).
  const roundStatusText = roundEndResolution || roundEndAnimating ? 'Resolving…' : roundSummary ? 'Completed' : null;
  const turnLabel = myTurn ? 'Your Turn' : `${opponentName}'s Turn`;

  const oasisStrengths = oasisRow.map((oasis, index) => {
    const myStrength = columnStrength(oasis.myColumn, oasis.opponentColumn, oasisRow, index, 'my');
    const opponentStrength = columnStrength(oasis.opponentColumn, oasis.myColumn, oasisRow, index, 'opponent');
    const winner = myStrength > opponentStrength ? 'my' : opponentStrength > myStrength ? 'opponent' : null;
    return { myStrength, opponentStrength, winner };
  });

  // The card being previewed for targeting: an active selection always
  // wins; otherwise, hovering a selectable hand card previews its options.
  const previewCardId = selectedCardId ?? (canSelectCard ? hoveredCardId : null);
  const previewCard = previewCardId ? view.hand.find(c => c.cardId === previewCardId) : null;

  const previewingMySide = Boolean(previewCard) && (inferredAction === null || inferredAction === 'A');
  const previewingOpponentSide = Boolean(previewCard) && inferredAction === null;

  function renderColumnZone(column, side, oasisIndex) {
    const isActiveTarget = side === 'my' ? previewingMySide : previewingOpponentSide;
    // Sized assuming the more permissive face-up placement -- if a 4th slot
    // only exists because the incoming card would be a face-up Jackal, the
    // target zone should still show (so a face-up placement there works);
    // the face-down option offered within it is separately disabled below
    // when it wouldn't actually fit.
    const capacity = isActiveTarget ? columnCapacity(column, previewCard.animal, 'up') : columnCapacity(column, null);
    const filledCount = column.length;
    const targetableEmptyCount = isActiveTarget ? Math.max(0, capacity - filledCount) : 0;
    // While awaiting a pull choice, this column (the one the card was just
    // placed into) reserves dashed "open" slots for however many more cards
    // are still to be pulled in.
    const isPullChoiceTargetColumn =
      pendingPullChoice &&
      side === pendingPullChoice.playDraft.side &&
      oasisIndex === pendingPullChoice.playDraft.oasisIndex;
    const openPullSlotCount = isPullChoiceTargetColumn
      ? pendingPullChoice.fitCount - pendingPullChoice.chosenCardIds.length
      : 0;
    // A card an unsubmitted Scorpion play would destroy is already gone
    // from `column` (so the capacity math above already accounts for its
    // freed slot), but it's spliced back in below for display, marked
    // pendingRemoval, so the player can see exactly what's about to
    // disappear -- so the locked-slot count must shrink to compensate,
    // keeping the column's total rendered height at CARD_SLOTS.
    const doomedHere = pendingRemovedCards.filter(r => r.side === side && r.oasisIndex === oasisIndex);
    const lockedEmptyCount = Math.max(
      0,
      CARD_SLOTS - filledCount - targetableEmptyCount - openPullSlotCount - doomedHere.length
    );

    const displayEntries = column.map(card => ({ card, pendingRemoval: false }));
    [...doomedHere]
      .sort((a, b) => a.index - b.index)
      .forEach(d => {
        displayEntries.splice(Math.min(d.index, displayEntries.length), 0, { card: d.card, pendingRemoval: true });
      });

    const filledSlots = displayEntries.map(({ card, pendingRemoval }) => {
      const pendingPlay = plays.find(p => p.cardId === card.cardId);
      const isDraftPlay = pendingPullChoice?.playDraft.cardId === card.cardId;
      // Only the sole card of the turn placed on my own side can still
      // choose face up/down in place (mirrors the initial placement rule).
      const canToggleFace = pendingPlay && side === 'my' && plays.length === 1 && !pendingRemoval;
      // A doomed card no longer has an index within `column` (the real,
      // already-reduced state) -- its effective strength doesn't affect
      // anything it's about to leave, so it's fine to skip.
      const realIndex = column.findIndex(c => c.cardId === card.cardId);
      const effectiveStrength =
        card.animal === 'chameleon' && realIndex !== -1
          ? resolveChameleonStrength(card, realIndex, oasisRow, oasisIndex, side)
          : undefined;
      // cardIds are unique game-wide, so membership alone identifies a card
      // from the most recent submitted move -- no need to also match which
      // zone it's rendered in, since e.g. an action-B play or a portal pull
      // can legitimately move a card into the opponent's column.
      const isLastSubmittedMove = lastMoveCardIds.includes(card.cardId);
      const pullCandidate = !pendingRemoval && pendingPullChoice?.eligible.find(e => e.cardId === card.cardId);
      const isChosenForPull = pendingPullChoice?.chosenCardIds.includes(card.cardId);
      // Mid-turn Scorpion tie (not to be confused with the round-end
      // scorpionTieBreak decision below, a completely separate flow) --
      // only the side currently being decided is clickable, one side at a
      // time, same as round-end resolves its own sides sequentially.
      const midTurnScorpionTie = pendingScorpionTieChoice?.ties.find(
        t => !pendingScorpionTieChoice.resolvedTieBreak[t.side] && t.side === side
      );
      const isMidTurnScorpionTieCandidate =
        !pendingRemoval && midTurnScorpionTie?.candidates.some(c => c.cardId === card.cardId);

      // Round-end decision candidates are highlighted on their real board
      // card (not pulled out into a separate strip) so the player is always
      // looking at the actual column. Caught up + it's genuinely my
      // decision (not the opponent's, and not still mid-animation).
      const roundEndActive = roundEndCaughtUp && roundEndPendingStep?.deciderSide === 'me';
      const isFlipOrderCandidate =
        roundEndActive &&
        roundEndPendingStep.kind === 'flipOrder' &&
        side === 'my' &&
        roundEndPendingStep.candidateCardIds.includes(card.cardId);
      const isRoundEndPullCandidate =
        roundEndActive &&
        roundEndPendingStep.kind === 'pullChoice' &&
        roundEndPendingStep.eligible.some(e => e.cardId === card.cardId);
      const isScorpionTieCandidate =
        roundEndActive &&
        roundEndPendingStep.kind === 'scorpionTieBreak' &&
        side === 'my' &&
        roundEndPendingStep.candidateCardIds.includes(card.cardId);
      const flipOrderChosen = isFlipOrderCandidate && flipOrderSelection.includes(card.cardId);
      const flipOrderPosition = flipOrderChosen ? flipOrderSelection.indexOf(card.cardId) + 1 : null;
      const roundEndPullChosen = isRoundEndPullCandidate && roundEndChosenPullIds.includes(card.cardId);
      // Not yet chosen: still genuinely face-down on the board, but a
      // player already knows their own hidden cards -- previewed face-up
      // here so they can see what they're choosing between, same as the
      // mid-turn peek gesture. Once chosen, `card` itself is already
      // face-up (and possibly relocated by a pull) in roundEndPreviewRow,
      // so no override is needed.
      // Transient animation-driven presentation, independent of the normal
      // pendingRemoval/pending-choice states above -- see killingCardIds/
      // flippingCard (derived from incomingAnimation's current step).
      const isAnimKilling = killingCardIds?.has(card.cardId);
      const isAnimFlipping = flippingCard?.cardId === card.cardId;
      const displayCard =
        isFlipOrderCandidate && !flipOrderChosen
          ? { ...card, face: 'up' }
          : isAnimFlipping
          ? { cardId: card.cardId, animal: flippingCard.animal, face: 'up', orientation: flippingCard.orientation }
          : card;

      return (
        <div
          className={`oasis-board-slot oasis-board-filled-slot${isAnimFlipping ? ' oasis-board-slot-flipping' : ''}`}
          key={card.cardId}
          style={isAnimFlipping ? { '--flip-duration': `${MOVE_ANIMATION_TIMING.flip.playMs}ms` } : undefined}
          ref={el => {
            if (el) cardNodeRefs.current.set(card.cardId, el);
            else cardNodeRefs.current.delete(card.cardId);
          }}
        >
          <AnimalCard
            card={displayCard}
            flipping={isAnimFlipping}
            mine={side === 'my'}
            pending={Boolean(pendingPlay) || isDraftPlay}
            clickable={
              !pendingRemoval &&
              ((Boolean(pendingPlay) && myTurn) ||
                Boolean(pullCandidate) ||
                (isFlipOrderCandidate && !flipOrderQueue) ||
                ((isRoundEndPullCandidate || isScorpionTieCandidate) && !roundEndSubmitting) ||
                isMidTurnScorpionTieCandidate)
            }
            pullCandidate={
              Boolean(pullCandidate) ||
              isFlipOrderCandidate ||
              isRoundEndPullCandidate ||
              isScorpionTieCandidate ||
              isMidTurnScorpionTieCandidate
            }
            pullChosen={isChosenForPull || flipOrderChosen || roundEndPullChosen}
            dimmed={isFlipOrderCandidate && !flipOrderChosen}
            pendingRemoval={pendingRemoval || isAnimKilling}
            effectiveStrength={effectiveStrength}
            highlight={!pendingPlay && !isDraftPlay && isLastSubmittedMove}
            onClick={() => {
              if (pendingRemoval) return;
              if (pullCandidate) {
                toggleChosenPullCard(card.cardId);
                return;
              }
              if (isFlipOrderCandidate) {
                toggleFlipOrderCard(card.cardId);
                return;
              }
              if (isRoundEndPullCandidate) {
                toggleRoundEndPull(card.cardId);
                return;
              }
              if (isScorpionTieCandidate) {
                submitRoundEndChoice('scorpionTieBreak', card.cardId);
                return;
              }
              if (isMidTurnScorpionTieCandidate) {
                chooseScorpionTie(card.cardId);
                return;
              }
              if (pendingPlay) handlePickUpPlay(pendingPlay);
            }}
          />
          {flipOrderPosition && (
            <div className="oasis-board-flip-order-badge" aria-hidden="true">
              {flipOrderPosition}
            </div>
          )}
          {canToggleFace && (
            <button
              type="button"
              className="oasis-board-toggle-face-button"
              disabled={pendingPlay.face === 'up' && !canPlayFaceDown}
              title={
                pendingPlay.face === 'up' && !canPlayFaceDown
                  ? 'You already have one more face-down card than your opponent'
                  : undefined
              }
              onClick={e => {
                e.stopPropagation();
                if (pendingPlay.face === 'up' && !canPlayFaceDown) return;
                toggleFace(pendingPlay.cardId);
              }}
            >
              {pendingPlay.face === 'down' ? 'Face Up' : 'Face Down'}
            </button>
          )}
        </div>
      );
    });
    // The very first card of the turn, placed on my own side, can still go
    // either face-up (partial action A) or face-down (action C) -- offer
    // both right in the target region. Everything else (opponent's side,
    // or the 2nd card of action A) is always face-up with a single click.
    const offerFaceDownChoice = side === 'my' && plays.length === 0;
    // The dashed target zone above is sized assuming a face-up placement
    // (see `capacity`), which can overstate room for a face-down one -- a
    // 4th slot that only exists because this card would be a face-up Jackal
    // isn't actually available face-down.
    const canFaceDownFit = isActiveTarget && filledCount < columnCapacity(column, previewCard?.animal, 'down');
    // Merging N empty slots into one flex item removes the (N-1) row gaps
    // that would otherwise sit between them, so the group's height must add
    // those gaps back in -- otherwise a column's total zone height would
    // depend on how many separate vs. merged items it happens to render
    // (e.g. 0 filled cards vs. 1 filled card), which is exactly the kind of
    // bottom-edge mismatch this is guarding against.
    const targetSlot = targetableEmptyCount > 0 && (
      <div
        key="target"
        className="oasis-board-slot oasis-board-empty-group oasis-board-target"
        style={{
          height: `calc(var(--slot-size) * ${targetableEmptyCount} + ${SLOT_GAP_REM}rem * ${targetableEmptyCount - 1})`,
        }}
      >
        <div
          className="oasis-board-target-clickarea"
          onClick={() => selectedCardId && handlePickTarget(side, oasisIndex, 'up')}
        />
        {offerFaceDownChoice && (
          <button
            type="button"
            className="oasis-board-face-down-button"
            disabled={!canPlayFaceDown || !canFaceDownFit}
            title={
              !canPlayFaceDown
                ? 'You already have one more face-down card than your opponent'
                : !canFaceDownFit
                ? 'This slot is only open for a face-up Jackal'
                : undefined
            }
            onClick={e => {
              e.stopPropagation();
              if (selectedCardId && canPlayFaceDown && canFaceDownFit) handlePickTarget(side, oasisIndex, 'down');
            }}
          >
            Face Down
          </button>
        )}
      </div>
    );
    const lockedSlots = Array.from({ length: lockedEmptyCount }).map((_, i) => (
      <div className="oasis-board-slot" key={`locked-${i}`} />
    ));
    const openPullSlots = Array.from({ length: openPullSlotCount }).map((_, i) => (
      <div className="oasis-board-slot oasis-board-target" key={`pull-open-${i}`} />
    ));

    if (side === 'my') {
      // Closest-to-oasis first (top of my zone, which sits just below the
      // oasis row): filled cards oldest-to-newest, then the open target
      // region, then any capacity-locked slots. New cards land right after
      // the existing ones, farther from the oasis.
      return [...filledSlots, ...openPullSlots, targetSlot, ...lockedSlots];
    }
    // Opponent zone sits above the oasis row, so closest-to-oasis is the
    // BOTTOM. The column array is still oldest-first, so it has to be
    // reversed here -- otherwise the oldest card would render above (i.e.
    // farther from the oasis than) a newly-placed one, when a new card
    // should always land farther out than whatever's already there.
    return [...lockedSlots, targetSlot, ...openPullSlots, ...[...filledSlots].reverse()];
  }

  return (
    <div className="oasis-board">
      <div className="oasis-board-header">
        <div className="oasis-board-opponent-info">
          <div className="oasis-board-opponent-name-row">
            <span className="oasis-board-opponent-name">{opponentName}</span>
            <span className="oasis-board-palm-trees">🌴 x{opponentPalmTreesRemaining}</span>
          </div>
          <div className="oasis-board-hand-mini" ref={handMiniRef}>
            {Array.from({ length: opponentHandCount }).map((_, i) => (
              <div key={i} className="animal-card-back animal-card-back-mini" />
            ))}
          </div>
        </div>
        <div className="oasis-board-status">
          <div className="oasis-board-turn-round">Round {round}</div>
          {roundStatusText && <div className="oasis-board-round-status">{roundStatusText}</div>}
          {myScore !== null && opponentScore !== null && (
            <div className="oasis-board-status-score">{myScore} to {opponentScore}</div>
          )}
          {myScore !== null && opponentScore !== null && (
            <div className="oasis-board-status-standing">
              {myScore > opponentScore ? 'Winning' : myScore < opponentScore ? 'Losing' : 'Tied'}
            </div>
          )}
        </div>
      </div>

      <div className="oasis-board-row">
        {/* Empty placeholder so the deck (below, in the oasis-card row)
            doesn't shift every other row's 5 columns out of alignment. */}
        <div key="deck-spacer-opp-cards" />
        {oasisRow.map((oasis, index) => (
          <div key={`opp-${oasis.oasisId}`} className="oasis-board-cards oasis-board-cards-opponent">
            {renderColumnZone(oasis.opponentColumn, 'opponent', index)}
          </div>
        ))}

        <div key="deck-spacer-opp-points" />
        {oasisRow.map((oasis, index) => {
          const { winner, opponentStrength } = oasisStrengths[index];
          const showBadge = revealResultNow;
          return (
            <div
              key={`pts-opp-${oasis.oasisId}`}
              className={`oasis-board-points${showBadge ? ' oasis-board-points-visible' : ''}${
                showBadge && winner === 'opponent' ? ' oasis-board-points-winning' : ''
              }`}
            >
              <span className="oasis-board-points-value">{showBadge ? opponentStrength : ''}</span>
            </div>
          );
        })}

        <div className="oasis-board-deck">
          {deckCount > 0 ? (
            <div className="animal-card">
              <div className="animal-card-back oasis-board-deck-count">{deckCount}</div>
            </div>
          ) : (
            <div className="oasis-board-deck-empty">
              <span className="oasis-board-deck-label">
                No cards
                <br />
                remaining
              </span>
            </div>
          )}
        </div>
        {oasisRow.map((oasis, index) => (
          <OasisCard
            key={oasis.oasisId}
            oasisId={oasis.oasisId}
            palmTreeOwnerIds={oasis.palmTreeOwnerIds}
            selectable={readyToSubmit && tripletOasisIndexes.length > 0}
            selected={palmTreeChoice === index}
            winner={revealResultNow ? oasisStrengths[index].winner : undefined}
            onClick={() => setPalmTreeChoice(current => (current === index ? null : index))}
          />
        ))}

        <div key="deck-spacer-my-points" />
        {oasisRow.map((oasis, index) => {
          const { winner, myStrength } = oasisStrengths[index];
          const showBadge = revealResultNow;
          return (
            <div
              key={`pts-mine-${oasis.oasisId}`}
              className={`oasis-board-points${showBadge ? ' oasis-board-points-visible' : ''}${
                showBadge && winner === 'my' ? ' oasis-board-points-winning' : ''
              }`}
            >
              <span className="oasis-board-points-value">{showBadge ? myStrength : ''}</span>
            </div>
          );
        })}

        <div key="deck-spacer-my-cards" />
        {oasisRow.map((oasis, index) => (
          <div key={`mine-${oasis.oasisId}`} className="oasis-board-cards oasis-board-cards-mine">
            {renderColumnZone(oasis.myColumn, 'my', index)}
          </div>
        ))}
      </div>

      {/* These all sit below the board rather than between it and the
          header, on purpose -- they come and go a lot (round-end status,
          move summaries, round summary, burned card), and appearing above
          the board would otherwise shift its position on screen every time
          one of them shows up or disappears mid-turn. */}
      {roundEndResolution && !flipOrderActive && !flipOrderSubmittingQueue && (
        <div className="oasis-board-round-end-row">
          {!roundEndCaughtUp ? (
            <div className="oasis-board-round-end-status">
              <span>Resolving the round…</span>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setRoundEndAnimatedCount(roundEndResolution.recentSteps.length)}
              >
                Skip ahead
              </Button>
            </div>
          ) : roundEndPendingStep?.deciderSide === 'opponent' ? (
            <div className="oasis-board-round-end-status">
              <span>Waiting for {opponentName} to choose…</span>
            </div>
          ) : roundEndPendingStep?.kind === 'pullChoice' ? (
            <div className="oasis-board-round-end-status">
              <span>
                Select which card(s) to pull ({roundEndChosenPullIds.length}/{roundEndPendingStep.fitCount})
              </span>
            </div>
          ) : roundEndPendingStep?.kind === 'scorpionTieBreak' ? (
            <div className="oasis-board-round-end-status">
              <span>The Scorpion strikes -- choose which of your cards is destroyed</span>
            </div>
          ) : (
            <div className="oasis-board-round-end-status">
              <span>Resolving the round…</span>
            </div>
          )}
        </div>
      )}

      {moveSummary && !roundEndResolution && (
        <div className="oasis-board-move-summary-row">
          <span>
            {moveSummary.parts.map((part, i) =>
              typeof part === 'string' ? (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={i}>{part}</React.Fragment>
              ) : (
                <MiniAnimalCard key={i} animal={part.card} faceDown={part.faceDown} />
              )
            )}
          </span>
          {lastReplay && lastReplay.key === moveSummary.key && (
            <Button size="sm" onClick={handleReplay} disabled={Boolean(incomingAnimation)}>
              Replay
            </Button>
          )}
        </div>
      )}

      {roundSummaryNow && (
        <div className="oasis-board-round-summary-row">
          <span>{roundSummaryNow}</span>
          {onNextRound && (
            <Button size="sm" onClick={onNextRound} disabled={nextRoundDisabled}>
              {nextRoundButtonLabel ?? 'Next Round'}
            </Button>
          )}
        </div>
      )}

      {revealResultNow && burnedCard && (
        <div className="oasis-board-burned-card">Burn Card: {ANIMAL_LABEL[burnedCard.animal]}</div>
      )}

      {pendingPullChoice && (
        <div className="oasis-board-palm-picker mb-2">
          <span>Multiple cards can be pulled in. Select which card(s) to pull</span>
        </div>
      )}

      {flipOrderActive && (
        <div className="oasis-board-palm-picker mb-2">
          <span>You have more than 1 of the same animal face down. Click them in the order you want them revealed.</span>
        </div>
      )}

      {pendingScorpionTieChoice && (
        <div className="oasis-board-palm-picker mb-2">
          <span>
            The Scorpion strikes -- choose which of{' '}
            {pendingScorpionTieChoice.ties.find(t => !pendingScorpionTieChoice.resolvedTieBreak[t.side])?.side ===
            'my'
              ? 'your'
              : `${opponentName}'s`}{' '}
            cards is destroyed
          </span>
        </div>
      )}

      {plays.some(p => view.hand.find(c => c.cardId === p.cardId)?.animal === 'chameleon') && (
        <div className="mb-2">
          {plays
            .filter(p => view.hand.find(c => c.cardId === p.cardId)?.animal === 'chameleon')
            .map(p => (
              <div key={p.cardId} className="mb-1">
                Chameleon orientation:{' '}
                <ButtonGroup size="sm">
                  <Button
                    variant={p.orientation === 'left' ? 'primary' : 'outline-primary'}
                    onClick={() => setOrientation(p.cardId, 'left')}
                  >
                    ←
                  </Button>
                  <Button
                    variant={p.orientation === 'right' ? 'primary' : 'outline-primary'}
                    onClick={() => setOrientation(p.cardId, 'right')}
                  >
                    →
                  </Button>
                </ButtonGroup>
              </div>
            ))}
        </div>
      )}

      {readyToSubmit && tripletOasisIndexes.length > 0 && (
        <div className="oasis-board-palm-picker mb-2">
          <span>3 in a row. Click the oasis to place a palm tree on or skip</span>
          <Button
            size="sm"
            variant={palmTreeChoice === null ? 'primary' : 'outline-primary'}
            onClick={() => setPalmTreeChoice(null)}
          >
            Skip
          </Button>
        </div>
      )}

      <div className="oasis-board-hand-area">
        <div className="oasis-board-controls-row">
          <div className="oasis-board-controls-left">
            <div className="oasis-board-my-name-row mb-2">
              <span className="oasis-board-my-name">{myName}</span>
              <span className="oasis-board-palm-trees">🌴 x{myPalmTreesRemaining}</span>
              {!roundStatusText && <span className="oasis-board-my-turn-indicator">{turnLabel}</span>}
            </div>

            <div className="oasis-board-hand">
              {view.hand.filter(card => !plays.some(p => p.cardId === card.cardId)).map(card => (
                <AnimalCard
                  key={card.cardId}
                  card={{ ...card, face: 'up' }}
                  mine
                  selected={selectedCardId === card.cardId}
                  playable={canSelectCard}
                  onClick={() => handleSelectHandCard(card.cardId)}
                  onHoverStart={
                    supportsHover ? () => canSelectCard && setHoveredCardId(card.cardId) : undefined
                  }
                  onHoverEnd={
                    supportsHover
                      ? () => setHoveredCardId(current => (current === card.cardId ? null : current))
                      : undefined
                  }
                />
              ))}
            </div>
          </div>

          <div className="oasis-board-controls-right">
            <Button
              variant="outline-primary"
              size="lg"
              className="oasis-board-submit-button"
              onClick={flipOrderActive ? () => setFlipOrderSelection([]) : resetTurn}
              disabled={
                flipOrderActive
                  ? flipOrderSelection.length === 0
                  : !myTurn ||
                    (plays.length === 0 && !pendingPullChoice && !pendingScorpionTieChoice) ||
                    Boolean(pendingSubmissionCardIds)
              }
            >
              Reset
            </Button>
            <Button
              size="lg"
              className="oasis-board-submit-button"
              disabled={
                flipOrderActive
                  ? flipOrderSelection.length !== roundEndPendingStep.candidateCardIds.length
                  : !readyToSubmit || submitting || Boolean(pendingSubmissionCardIds)
              }
              onClick={flipOrderActive ? () => setFlipOrderQueue(flipOrderSelection) : handleSubmit}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
